import React, { useEffect } from 'react';
import { Mountain, ArrowLeft, Cloud, Building2, ExternalLink } from 'lucide-react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { blogPosts, getBlogPost, ContentBlock } from '../../data/blogPosts';

const categoryMeta: Record<string, { icon: React.FC<{ className?: string }>; color: string; bg: string; border: string }> = {
  'Roam Weather': {
    icon: Cloud,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    border: 'border-blue-200',
  },
  'Roam Office Tracker': {
    icon: Building2,
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    border: 'border-orange-200',
  },
};

function renderBlock(block: ContentBlock, index: number): React.ReactNode {
  switch (block.type) {
    case 'h2':
      return (
        <h2 key={index} className="text-2xl font-bold text-gray-900 mt-10 mb-4">
          {block.text}
        </h2>
      );
    case 'h3':
      return (
        <h3 key={index} className="text-xl font-semibold text-gray-900 mt-6 mb-2">
          {block.text}
        </h3>
      );
    case 'p':
      return (
        <p key={index} className="text-gray-700 leading-relaxed mb-4">
          {block.text}
        </p>
      );
    case 'ul':
      return (
        <ul key={index} className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 mb-4 pl-2">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'image':
      return (
        <figure key={index} className="my-8">
          <img
            src={block.src}
            alt={block.alt}
            loading="lazy"
            className="w-full rounded-xl border border-gray-200 shadow-sm"
          />
          {block.caption && (
            <figcaption className="text-sm text-gray-500 text-center mt-2">{block.caption}</figcaption>
          )}
        </figure>
      );
    case 'callout':
      return (
        <div key={index} className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-10">
          <p className="text-gray-800 font-medium mb-3">{block.text}</p>
          <a
            href={block.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
          >
            {block.linkText}
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      );
    default:
      return null;
  }
}

export const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Roam Blog`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', post.description);
      }
    }
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const cat = categoryMeta[post.category];
  const Icon = cat?.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
                <Mountain className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Roam</h1>
                <p className="text-sm text-gray-600">Outdoor Adventure Apps</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-colors">
                Blog
              </Link>
              <a
                href="https://weather.roamapps.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Try Weather
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Article */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link
          to="/blog"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Link>

        <article className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 sm:p-12">
          {/* Category + meta */}
          <div className="flex items-center gap-3 mb-6">
            {Icon && (
              <div className={`${cat.bg} p-1.5 rounded-lg`}>
                <Icon className={`h-4 w-4 ${cat.color}`} />
              </div>
            )}
            <span className={`text-sm font-medium ${cat?.color ?? 'text-gray-600'}`}>
              {post.category}
            </span>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-400">{post.date}</span>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-400">{post.readTime}</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Description / lede */}
          <p className="text-xl text-gray-500 mb-8 leading-relaxed border-l-4 border-blue-200 pl-4">
            {post.description}
          </p>

          {/* Content */}
          <div className="prose-content">
            {post.content.map((block, i) => renderBlock(block, i))}
          </div>
        </article>

        {/* Related posts */}
        {(() => {
          const related = blogPosts.filter((p) => p.slug !== post.slug);
          if (related.length === 0) return null;
          return (
            <div className="mt-12">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">More from the Roam Blog</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((rel) => {
                  const relCat = categoryMeta[rel.category];
                  const RelIcon = relCat?.icon;
                  return (
                    <Link
                      key={rel.slug}
                      to={`/blog/${rel.slug}`}
                      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {RelIcon && (
                          <div className={`${relCat.bg} p-1 rounded`}>
                            <RelIcon className={`h-3.5 w-3.5 ${relCat.color}`} />
                          </div>
                        )}
                        <span className={`text-xs font-medium ${relCat?.color ?? 'text-gray-500'}`}>
                          {rel.category}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">
                        {rel.title}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">© 2026 Roam. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/" className="text-gray-400 hover:text-white text-sm transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Blog
              </Link>
              <Link to="/support" className="text-gray-400 hover:text-white text-sm transition-colors">
                Support
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
